import { IconName } from '@fortawesome/fontawesome-common-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { getGetPrams } from '../../../utils/url'
import COLOR_LIST from '../../constants/taskColor'
import SearchAndSelect from './SearchAndSelect'
import { quoteSplit } from '../../../utils/string'

interface Props {
  callbackTeamIds?: (teamIds: number[]) => void
  callbackMemberIds?: (memberIds: number[]) => void
  callbackKeywords?: (keywords: string[]) => void
  callbackLabelMasterKeywords?: (labelMasterKeywords: string[]) => void
  callbackKey?: (key: string) => void
  callbackDirection?: (direction: string) => void
  callbackDisabled?: (disabled: boolean) => void
  callbackPageOffset?: (page: number) => void
  callbackLabelGroupIds?: (labelGroupIds: number[]) => void
  callbackMasterRelationLabelGroupIds?: (masterRelationLabelGroupList: number[]) => void
  callbackMasterRelationLabelGroupDisabled?: (disabled: boolean) => void
  callbackColorLabels?: (colorLabels: string[]) => void
  callbackCreatorMemberIds?: (memberIds: number[]) => void
  callbackViewerMemberIds?: (memberIds: number[]) => void
  callbackUserMemberIds?: (memberIds: number[]) => void
  memberList?: { id: number, name: string }[]
  teamList?: { id: number, name: string }[]
  labelGroupList?: { id: number, name: string }[]
  masterRelationLabelGroupList?: { id: number, name: string }[]
  isClear?: boolean
}

interface GetParams {
  team_ids: string
  member_ids: string
  label_group_ids: string
  master_relation_label_group_ids: string
  only_unassigned: string
  keywords: string
  label_master_keywords: string
  key: string
  direction: string
  disabled: string
  colors: string
  creator_member_ids: string
  viewer_member_ids: string
  user_member_ids: string
}

const SearchModule: FC<Props> = (props) => {
  const {
    callbackTeamIds,
    callbackMemberIds,
    callbackKeywords,
    callbackLabelMasterKeywords,
    callbackMasterRelationLabelGroupIds,
    callbackMasterRelationLabelGroupDisabled,
    callbackKey,
    callbackDirection,
    callbackDisabled,
    callbackPageOffset,
    callbackLabelGroupIds,
    callbackColorLabels,
    callbackCreatorMemberIds,
    callbackViewerMemberIds,
    callbackUserMemberIds,
    memberList,
    teamList,
    labelGroupList,
    masterRelationLabelGroupList,
    isClear,
  } = props

  const {
    task,
    login,
  } = useSelector((state: RootState) => state)

  const [isSearchDisplay, setIsSearchDisplay] = useState(false)
  const [isClickable, setIsClickable] = useState(true)

  const [teamIds, setTeamIds] = useState<number[]>([])
  const [memberIds, setMemberIds] = useState<number[]>([])
  const [labelGroupIds, setLabelGroupIds] = useState<number[]>([])
  const [masterRelationLabelGroupIds, setMasterRelationLabelGroupIds] = useState<number[]>([])
  const [masterRelationLabelGroupDisabled, setMasterRelationLabelGroupDisabled] = useState<boolean>(false)
  const [colorLabels, setColorLabels] = useState<string[]>([])
  const [creatorMemberIds, setCreatorMemberIds] = useState<number[]>([])
  const [viewerMemberIds, setViewerMemberIds] = useState<number[]>([])
  const [userMemberIds, setUserMemberIds] = useState<number[]>([])
  const [searchKeyword, setSearchKeyword] = useState<string>("")
  const [labelMasterKeyword, setLabelMasterKeyword] = useState<string>("")
  const [searchDisabled, setSearchDisabled] = useState<boolean>(false)
  const [sortKey, setSortKey] = useState<string>("order_index")
  const [sortDirection, setSortDirection] = useState<string>("asc")

  const [initTeamIds, setInitTeamIds] = useState<number[]>([])
  const [initMemberIds, setInitMemberIds] = useState<number[]>([])
  const [initLabelGroupIds, setInitLabelGroupIds] = useState<number[]>([])
  const [initMasterRelationLabelGroupIds, setInitMasterRelationLabelGroupIds] = useState<number[]>([])
  const [initMasterRelationLabelGroupDisabled, setInitMasterRelationLabelGroupDisabled] = useState<boolean>(false)
  const [initColorLabels, setInitColorLabels] = useState<string[]>([])
  const [initCreatorMemberIds, setInitCreatorMemberIds] = useState<number[]>([])
  const [initViewerMemberIds, setInitViewerMemberIds] = useState<number[]>([])
  const [initUserMemberIds, setInitUserMemberIds] = useState<number[]>([])
  const [initSearchKeyword, setInitSearchKeyword] = useState<string>("")
  const [initLabelMasterKeyword, setInitLabelMasterKeyword] = useState<string>("")
  const [initSearchDisabled, setInitSearchDisabled] = useState<boolean>(false)
  const [initSortKey, setInitSortKey] = useState<string>("order_index")
  const [initSortDirection, setInitSortDirection] = useState<string>("asc")

  const isCanSubmit = useMemo(() => {
    if (
      searchKeyword === initSearchKeyword
      && labelMasterKeyword === initLabelMasterKeyword
      && teamIds.sort().join(',') === initTeamIds.sort().join(',')
      && memberIds.sort().join(',') === initMemberIds.sort().join(',')
      && labelGroupIds.sort().join(',') === initLabelGroupIds.sort().join(',')
      && masterRelationLabelGroupIds.sort().join(',') === initMasterRelationLabelGroupIds.sort().join(',')
      && masterRelationLabelGroupDisabled === initMasterRelationLabelGroupDisabled
      && colorLabels.sort().join(',') === initColorLabels.sort().join(',')
      && creatorMemberIds.sort().join(',') === initCreatorMemberIds.sort().join(',')
      && viewerMemberIds.sort().join(',') === initViewerMemberIds.sort().join(',')
      && userMemberIds.sort().join(',') === initUserMemberIds.sort().join(',')
      && searchDisabled === initSearchDisabled
      && sortKey === initSortKey
      && sortDirection === initSortDirection
    ) {
      return false
    }
    return true
  }, [
    searchKeyword,
    labelMasterKeyword,
    teamIds,
    memberIds,
    labelGroupIds,
    masterRelationLabelGroupIds,
    masterRelationLabelGroupDisabled,
    colorLabels,
    creatorMemberIds,
    viewerMemberIds,
    userMemberIds,
    searchDisabled,
    sortKey,
    sortDirection,
  ])

  const clickTeam = (team_id: number) => {
    if (teamIds.includes(team_id)) {
      setTeamIds(teamIds.filter((id) => id !== team_id))
    } else {
      setTeamIds([...teamIds, team_id])
    }
  }

  const clickMember = (member_id: number) => {
    if (memberIds.includes(member_id)) {
      setMemberIds(memberIds.filter((id) => id !== member_id))
    } else {
      setMemberIds([...memberIds, member_id])
    }
  }

  const clickLabelGroup = (label_group_id: number) => {
    if (labelGroupIds.includes(label_group_id)) {
      setLabelGroupIds(labelGroupIds.filter((id) => id !== label_group_id))
    } else {
      setLabelGroupIds([...labelGroupIds, label_group_id])
    }
  }

  const clickMasterRelationLabelGroup = (label_group_id: number) => {
    if (masterRelationLabelGroupIds.includes(label_group_id)) {
      setMasterRelationLabelGroupIds(masterRelationLabelGroupIds.filter((id) => id !== label_group_id))
    } else {
      setMasterRelationLabelGroupIds([...masterRelationLabelGroupIds, label_group_id])
    }
  }

  const clickColor = (color: string) => {
    if (colorLabels.includes(color)) {
      setColorLabels(colorLabels.filter((code) => code !== color))
    } else {
      setColorLabels([...colorLabels, color])
    }
  }

  const clickCreatorMember = (member_id: number) => {
    if (creatorMemberIds.includes(member_id)) {
      setCreatorMemberIds(creatorMemberIds.filter((id) => id !== member_id))
    } else {
      setCreatorMemberIds([...creatorMemberIds, member_id])
    }
  }

  const clickViewerMember = (member_id: number) => {
    if (viewerMemberIds.includes(member_id)) {
      setViewerMemberIds(viewerMemberIds.filter((id) => id !== member_id))
    } else {
      setViewerMemberIds([...viewerMemberIds, member_id])
    }
  }

  const clickUserMember = (member_id: number) => {
    if (userMemberIds.includes(member_id)) {
      setUserMemberIds(userMemberIds.filter((id) => id !== member_id))
    } else {
      setUserMemberIds([...userMemberIds, member_id])
    }
  }

  const openSearch = () => {
    setIsSearchDisplay(true)
  }

  const cancelSearch = () => {
    setIsSearchDisplay(false)
  }

  const resetSearch = () => {
    setIsSearchDisplay(false)
    setTeamIds([])
    setMemberIds([])
    setLabelGroupIds([])
    setMasterRelationLabelGroupIds([])
    setMasterRelationLabelGroupDisabled(false)
    setSearchKeyword("")
    setLabelMasterKeyword("")
    setSearchDisabled(false)
    setSortKey(login.permissions?.high_volume ? "id" : "order_index")
    setSortDirection(login.permissions?.high_volume ? "desc" : "asc")
    setColorLabels([])
    setCreatorMemberIds([])
    setViewerMemberIds([])
    setUserMemberIds([])
    if (callbackTeamIds) callbackTeamIds([])
    if (callbackMemberIds) callbackMemberIds([])
    if (callbackLabelGroupIds) callbackLabelGroupIds([])
    if (callbackMasterRelationLabelGroupIds) callbackMasterRelationLabelGroupIds([])
    if (callbackMasterRelationLabelGroupDisabled) callbackMasterRelationLabelGroupDisabled(false)
    if (callbackKeywords) callbackKeywords([])
    if (callbackLabelMasterKeywords) callbackLabelMasterKeywords([])
    if (callbackKey) callbackKey(login.permissions?.high_volume ? "id" : "order_index")
    if (callbackDirection) callbackDirection(login.permissions?.high_volume ? "desc" : "asc")
    if (callbackDisabled) callbackDisabled(false)
    if (callbackPageOffset) callbackPageOffset(1)
    if (callbackColorLabels) callbackColorLabels([])
    if (callbackCreatorMemberIds) callbackCreatorMemberIds([])
    if (callbackViewerMemberIds) callbackViewerMemberIds([])
    if (callbackUserMemberIds) callbackUserMemberIds([])
  }

  const submitSearch = () => {
    const getParams = getGetPrams(location.search) as GetParams
    setIsSearchDisplay(false)
    const getParamTeamIds = getParams.team_ids ? getParams.team_ids.split(',').map((v) => Number(v)) : []
    if (
      callbackTeamIds && (
        (getParamTeamIds.filter((value) => teamIds.indexOf(value) === -1).length)
        || (teamIds.filter((value) => getParamTeamIds.indexOf(value) === -1).length)
      )
    ) {
      callbackTeamIds(teamIds)
    }
    const getParamMemberIds = getParams.member_ids ? getParams.member_ids.split(',').map((v) => Number(v)) : []
    if (
      callbackMemberIds && (
        (getParamMemberIds.filter((value) => memberIds.indexOf(value) === -1).length)
        || (memberIds.filter((value) => getParamMemberIds.indexOf(value) === -1).length)
      )
    ) {
      callbackMemberIds(memberIds)
    }
    const getParamLabelGroupIds = getParams.label_group_ids ? getParams.label_group_ids.split(',').map((v) => Number(v)) : []
    if (
      callbackLabelGroupIds && (
        (getParamLabelGroupIds.filter((value) => labelGroupIds.indexOf(value) === -1).length)
        || (labelGroupIds.filter((value) => getParamLabelGroupIds.indexOf(value) === -1).length)
      )
    ) {
      callbackLabelGroupIds(labelGroupIds)
    }
    const getParamMasterRelationLabelGroupIds = getParams.master_relation_label_group_ids
      ? getParams.master_relation_label_group_ids.split(',').map((v) => Number(v))
      : []
    if (
      callbackMasterRelationLabelGroupIds && (
        (getParamMasterRelationLabelGroupIds.filter((value) => masterRelationLabelGroupIds.indexOf(value) === -1).length)
        || (masterRelationLabelGroupIds.filter((value) => getParamMasterRelationLabelGroupIds.indexOf(value) === -1).length)
      )
    ) {
      callbackMasterRelationLabelGroupIds(masterRelationLabelGroupIds)
    }
    if (callbackMasterRelationLabelGroupDisabled) {
      callbackMasterRelationLabelGroupDisabled(masterRelationLabelGroupDisabled)
    }
    const getParamKeywords = getParams.keywords ? getParams.keywords.split(',') : []
    const tmpKeywords = quoteSplit(searchKeyword).filter((v) => v)
    if (
      callbackKeywords && (
        (getParamKeywords.filter((value) => tmpKeywords.indexOf(value) === -1).length)
        || (tmpKeywords.filter((value) => getParamKeywords.indexOf(value) === -1).length)
      )
    ) {
      callbackKeywords(tmpKeywords)
    }
    const getParamLabelMasterKeywords = getParams.label_master_keywords ? getParams.label_master_keywords.split(',') : []
    const tmpLabelMasterKeywords = quoteSplit(labelMasterKeyword).filter((v) => v)
    if (
      callbackLabelMasterKeywords && (
        (getParamLabelMasterKeywords.filter((value) => tmpLabelMasterKeywords.indexOf(value) === -1).length)
        || (tmpLabelMasterKeywords.filter((value) => getParamLabelMasterKeywords.indexOf(value) === -1).length)
      )
    ) {
      callbackLabelMasterKeywords(tmpLabelMasterKeywords)
    }
    if (callbackKey) {
      callbackKey(sortKey)
    }
    if (callbackDirection) {
      callbackDirection(sortDirection)
    }
    if (callbackDisabled) {
      callbackDisabled(searchDisabled)
    }
    if (callbackPageOffset) {
      callbackPageOffset(1)
    }
    if (callbackColorLabels) {
      callbackColorLabels(colorLabels)
    }
    const getParamCreatorMemberIds = getParams.creator_member_ids ? getParams.creator_member_ids.split(',').map((v) => Number(v)) : []
    if (
      callbackCreatorMemberIds && (
        (getParamCreatorMemberIds.filter((value) => creatorMemberIds.indexOf(value) === -1).length)
        || (creatorMemberIds.filter((value) => getParamCreatorMemberIds.indexOf(value) === -1).length)
      )
    ) {
      callbackCreatorMemberIds(creatorMemberIds)
    }
    const getParamViewerMemberIds = getParams.viewer_member_ids ? getParams.viewer_member_ids.split(',').map((v) => Number(v)) : []
    if (
      callbackViewerMemberIds && (
        (getParamViewerMemberIds.filter((value) => viewerMemberIds.indexOf(value) === -1).length)
        || (viewerMemberIds.filter((value) => getParamViewerMemberIds.indexOf(value) === -1).length)
      )
    ) {
      callbackViewerMemberIds(viewerMemberIds)
    }
    const getParamUserMemberIds = getParams.user_member_ids ? getParams.user_member_ids.split(',').map((v) => Number(v)) : []
    if (
      callbackUserMemberIds && (
        (getParamUserMemberIds.filter((value) => userMemberIds.indexOf(value) === -1).length)
        || (userMemberIds.filter((value) => getParamUserMemberIds.indexOf(value) === -1).length)
      )
    ) {
      callbackUserMemberIds(userMemberIds)
    }
  }

  useEffect(() => {
    const html = document.querySelector('html')
    if (!html) return
    if (isSearchDisplay) {
      html.classList.add('modal_open')
    } else {
      html.classList.remove('modal_open')
    }
  }, [isSearchDisplay])

  useEffect(() => {
    if (!isSearchDisplay) return
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.team_ids) {
      setTeamIds(getParams.team_ids.split(',').map((value) => Number(value)))
      setInitTeamIds(getParams.team_ids.split(',').map((value) => Number(value)))
    } else {
      setTeamIds([])
      setInitTeamIds([])
    }
    if (getParams.member_ids) {
      setMemberIds(getParams.member_ids.split(',').map((value) => Number(value)))
      setInitMemberIds(getParams.member_ids.split(',').map((value) => Number(value)))
    } else {
      setMemberIds([])
      setInitMemberIds([])
    }
    if (getParams.label_group_ids) {
      setLabelGroupIds(getParams.label_group_ids.split(',').map((value) => Number(value)))
      setInitLabelGroupIds(getParams.label_group_ids.split(',').map((value) => Number(value)))
    } else {
      setLabelGroupIds([])
      setInitLabelGroupIds([])
    }
    if (getParams.master_relation_label_group_ids) {
      setMasterRelationLabelGroupIds(getParams.master_relation_label_group_ids.split(',').map((value) => Number(value)))
      setInitMasterRelationLabelGroupIds(getParams.master_relation_label_group_ids.split(',').map((value) => Number(value)))
    } else {
      setMasterRelationLabelGroupIds([])
      setInitMasterRelationLabelGroupIds([])
    }
    if (getParams.only_unassigned) {
      setMasterRelationLabelGroupDisabled(JSON.parse(getParams.only_unassigned.toLowerCase()))
      setInitMasterRelationLabelGroupDisabled(JSON.parse(getParams.only_unassigned.toLocaleLowerCase()))
    } else {
      setMasterRelationLabelGroupDisabled(false)
      setInitMasterRelationLabelGroupDisabled(false)
    }
    if (getParams.keywords) {
      const tmpKeywords = decodeURIComponent(getParams.keywords).split(',').map((v) => (v.includes(' ') ? `"${v}"` : v))
      setSearchKeyword(tmpKeywords.join(' '))
      setInitSearchKeyword(tmpKeywords.join(' '))
    } else {
      setSearchKeyword("")
      setInitSearchKeyword("")
    }
    if (getParams.label_master_keywords) {
      const tmpLabelMasterKeywords = decodeURIComponent(getParams.label_master_keywords).split(',').map((v) => (v.includes(' ') ? `"${v}"` : v))
      setLabelMasterKeyword(tmpLabelMasterKeywords.join(' '))
      setInitLabelMasterKeyword(tmpLabelMasterKeywords.join(' '))
    } else {
      setLabelMasterKeyword("")
      setInitLabelMasterKeyword("")
    }
    if (getParams.disabled) {
      setSearchDisabled(JSON.parse(getParams.disabled.toLowerCase()))
      setInitSearchDisabled(JSON.parse(getParams.disabled.toLowerCase()))
    } else {
      setSearchDisabled(false)
      setInitSearchDisabled(false)
    }
    if (getParams.key) {
      setSortKey(getParams.key)
      setInitSortKey(getParams.key)
    } else {
      setSortKey(login.permissions?.high_volume ? "id" : "order_index")
      setInitSortKey(login.permissions?.high_volume ? "id" : "order_index")
    }
    if (getParams.direction) {
      setSortDirection(getParams.direction)
      setInitSortDirection(getParams.direction)
    } else {
      setSortDirection(login.permissions?.high_volume ? "desc" : "asc")
      setInitSortDirection(login.permissions?.high_volume ? "desc" : "asc")
    }
    if (getParams.colors) {
      setColorLabels(getParams.colors.split(','))
      setInitColorLabels(getParams.colors.split(','))
    } else {
      setColorLabels([])
      setInitColorLabels([])
    }
    if (getParams.creator_member_ids) {
      setCreatorMemberIds(getParams.creator_member_ids.split(',').map((value) => Number(value)))
      setInitCreatorMemberIds(getParams.creator_member_ids.split(',').map((value) => Number(value)))
    } else {
      setCreatorMemberIds([])
      setInitCreatorMemberIds([])
    }
    if (getParams.viewer_member_ids) {
      setViewerMemberIds(getParams.viewer_member_ids.split(',').map((value) => Number(value)))
      setInitViewerMemberIds(getParams.viewer_member_ids.split(',').map((value) => Number(value)))
    } else {
      setViewerMemberIds([])
      setInitViewerMemberIds([])
    }
    if (getParams.user_member_ids) {
      setUserMemberIds(getParams.user_member_ids.split(',').map((value) => Number(value)))
      setInitUserMemberIds(getParams.user_member_ids.split(',').map((value) => Number(value)))
    } else {
      setUserMemberIds([])
      setInitUserMemberIds([])
    }
  }, [isSearchDisplay])

  useEffect(() => {
    if (!isClear) return
    setTeamIds([])
    setMemberIds([])
    setLabelGroupIds([])
    setMasterRelationLabelGroupIds([])
    setMasterRelationLabelGroupDisabled(false)
    setSearchKeyword("")
    setSearchDisabled(false)
    setLabelMasterKeyword("")
    setSortKey(login.permissions?.high_volume ? "id" : "order_index")
    setSortDirection(login.permissions?.high_volume ? "desc" : "asc")
    setColorLabels([])
    setCreatorMemberIds([])
    setViewerMemberIds([])
    setUserMemberIds([])

    setInitTeamIds([])
    setInitMemberIds([])
    setInitLabelGroupIds([])
    setInitMasterRelationLabelGroupIds([])
    setInitMasterRelationLabelGroupDisabled(false)
    setInitSearchKeyword("")
    setInitSearchDisabled(false)
    setInitLabelMasterKeyword("")
    setInitSortKey(login.permissions?.high_volume ? "id" : "order_index")
    setInitSortDirection(login.permissions?.high_volume ? "desc" : "asc")
    setInitColorLabels([])
    setInitCreatorMemberIds([])
    setInitViewerMemberIds([])
    setInitUserMemberIds([])
  }, [isClear])

  useEffect(() => {
    setIsClickable(!task.isLoading)
  }, [task.isLoading])

  return (
    <>
      <button
        className="search_button"
        type="button"
        onClick={() => openSearch()}
        disabled={!isClickable}
      >
        <FontAwesomeIcon icon={['fas', 'search']} />
        &nbsp;
        {
          callbackKey && callbackDirection ? (
            <>
              絞り込み・ソート
            </>
          ) : (
            <>
              絞り込み
            </>
          )
        }
      </button>
      <div className={`search_box ${isSearchDisplay || 'none'}`}>
        <div className="search_detail">
          {
            callbackKeywords ? (
              <div className="category">
                <div className="category_title">
                  プロジェクト名・プロジェクトコードで絞り込み
                  {
                    searchKeyword ? (
                      <div
                        className="select_reset"
                        role="none"
                        onClick={() => setSearchKeyword("")}
                      >
                        リセット
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
                <input
                  type="text"
                  placeholder="検索したいプロジェクト名・プロジェクトコードを入力してください"
                  onChange={(event) => setSearchKeyword(event.target.value)}
                  value={searchKeyword}
                  style={{
                    width: "100%",
                  }}
                />
              </div>
            ) : (
              <></>
            )
          }
          {
            callbackLabelMasterKeywords ? (
              <div className="category">
                <div className="category_title">
                  業務名で絞り込み
                  {
                    labelMasterKeyword ? (
                      <div
                        className="select_reset"
                        role="none"
                        onClick={() => setLabelMasterKeyword("")}
                      >
                        リセット
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
                <input
                  type="text"
                  placeholder="検索したい業務名を入力してください"
                  onChange={(event) => setLabelMasterKeyword(event.target.value)}
                  value={labelMasterKeyword}
                  style={{
                    width: "100%",
                  }}
                />
              </div>
            ) : (
              <></>
            )
          }
          {
            callbackTeamIds ? (
              <SearchAndSelect
                categoryTitle="チームで絞り込み"
                searchPlaceholder="キーワードでチームを検索・選択してください"
                originalList={teamList || []}
                selectList={teamIds}
                callbackClick={(ele) => clickTeam(Number(ele))}
                callbackResetSelect={() => setTeamIds([])}
                keywordReset={isSearchDisplay}
              />
            ) : (
              <></>
            )
          }
          {
            callbackMemberIds ? (
              <SearchAndSelect
                categoryTitle="メンバーで絞り込み"
                searchPlaceholder="キーワードでメンバーを検索・選択してください"
                originalList={memberList || []}
                selectList={memberIds}
                callbackClick={(ele) => clickMember(Number(ele))}
                callbackResetSelect={() => setMemberIds([])}
                keywordReset={isSearchDisplay}
              />
            ) : (
              <></>
            )
          }
          {
            callbackLabelGroupIds ? (
              <SearchAndSelect
                categoryTitle="プロジェクト名・プロジェクトコードで絞り込み"
                searchPlaceholder="キーワードでプロジェクト名を検索・選択してください"
                originalList={labelGroupList || []}
                selectList={labelGroupIds}
                callbackClick={(ele) => clickLabelGroup(Number(ele))}
                callbackResetSelect={() => setLabelGroupIds([])}
                keywordReset={isSearchDisplay}
              />
            ) : (
              <></>
            )
          }
          {
            callbackMasterRelationLabelGroupIds ? (
              <SearchAndSelect
                categoryTitle="プロジェクト名・プロジェクトコードで絞り込み"
                searchPlaceholder="キーワードでプロジェクト名を検索・選択してください"
                originalList={masterRelationLabelGroupList || []}
                selectList={masterRelationLabelGroupIds}
                callbackClick={(ele) => clickMasterRelationLabelGroup(Number(ele))}
                callbackResetSelect={() => {
                  setMasterRelationLabelGroupIds([])
                  setMasterRelationLabelGroupDisabled(false)
                }}
                keywordReset={isSearchDisplay}
                disableOption={{
                  text: "プロジェクトに紐づいていない業務のみ表示する",
                  state: masterRelationLabelGroupDisabled,
                  callback: setMasterRelationLabelGroupDisabled,
                }}
              />
            ) : (
              <></>
            )
          }
          {
            callbackDisabled ? (
              <div className="category">
                <div className="category_title">
                  プロジェクトの状態
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                      }}
                      onClick={() => setSearchDisabled(!searchDisabled)}
                      role="none"
                    >
                      <FontAwesomeIcon
                        icon={['far', `${!searchDisabled ? 'dot-circle' : 'circle'}` as IconName]}
                        className={`icon ${!searchDisabled ? "checked" : "no_check"}`}
                        style={{
                          fontSize: "18px",
                          color: !searchDisabled ? "#08984C" : "#666666",
                        }}
                      />
                      未完了
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                      }}
                      onClick={() => setSearchDisabled(!searchDisabled)}
                      role="none"
                    >
                      <FontAwesomeIcon
                        icon={['far', `${searchDisabled ? 'dot-circle' : 'circle'}` as IconName]}
                        className={`icon ${searchDisabled ? "checked" : "no_check"}`}
                        style={{
                          fontSize: "18px",
                          color: searchDisabled ? "#08984C" : "#666666",
                        }}
                      />
                      完了
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <></>
            )
          }
          {
            callbackKey && callbackDirection ? (
              <div className="category">
                <div className="category_title">
                  ソート
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                  }}
                >
                  <select
                    style={{
                      border: "solid 1px #ccc",
                      borderRadius: "4px",
                      padding: "8px",
                    }}
                    onChange={(event) => setSortKey(event.target.value)}
                    value={sortKey}
                  >
                    <option value="id">
                      プロジェクト作成日
                    </option>
                    {
                      !login.permissions?.high_volume && (
                        <option value="order_index">
                          カスタム設定
                        </option>
                      )
                    }
                    <option value="scheduled_start_date">
                      プロジェクト開始日
                    </option>
                    <option value="scheduled_end_date">
                      プロジェクト終了予定日
                    </option>
                    <option value="disabled_at">
                      プロジェクト完了日
                    </option>
                    <option value="name">
                      プロジェクト名
                    </option>
                    <option value="code">
                      プロジェクトコード
                    </option>
                  </select>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                      }}
                      onClick={() => setSortDirection("asc")}
                      role="none"
                    >
                      <FontAwesomeIcon
                        icon={['far', `${sortDirection === "asc" ? 'dot-circle' : 'circle'}` as IconName]}
                        className={`icon ${sortDirection === "asc" ? "checked" : "no_check"}`}
                        style={{
                          fontSize: "18px",
                          color: sortDirection === "asc" ? "#08984C" : "#666666",
                        }}
                      />
                      昇順
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                      }}
                      onClick={() => setSortDirection("desc")}
                      role="none"
                    >
                      <FontAwesomeIcon
                        icon={['far', `${sortDirection === "desc" ? 'dot-circle' : 'circle'}` as IconName]}
                        className={`icon ${sortDirection === "desc" ? "checked" : "no_check"}`}
                        style={{
                          fontSize: "18px",
                          color: sortDirection === "desc" ? "#08984C" : "#666666",
                        }}
                      />
                      降順
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <></>
            )
          }
          {
            callbackCreatorMemberIds ? (
              <SearchAndSelect
                categoryTitle="作成者で絞り込み"
                searchPlaceholder="キーワードで作成者を検索・選択してください"
                originalList={memberList || []}
                selectList={creatorMemberIds}
                callbackClick={(ele) => clickCreatorMember(Number(ele))}
                callbackResetSelect={() => setCreatorMemberIds([])}
                keywordReset={isSearchDisplay}
              />
            ) : (
              <></>
            )
          }
          {
            callbackViewerMemberIds ? (
              <SearchAndSelect
                categoryTitle="表示メンバーで絞り込み"
                searchPlaceholder="キーワードで表示メンバーを検索・選択してください"
                originalList={memberList || []}
                selectList={viewerMemberIds}
                callbackClick={(ele) => clickViewerMember(Number(ele))}
                callbackResetSelect={() => setViewerMemberIds([])}
                keywordReset={isSearchDisplay}
              />
            ) : (
              <></>
            )
          }
          {
            callbackUserMemberIds ? (
              <SearchAndSelect
                categoryTitle="参加メンバーで絞り込み"
                searchPlaceholder="キーワードで参加メンバーを検索・選択してください"
                originalList={memberList || []}
                selectList={userMemberIds}
                callbackClick={(ele) => clickUserMember(Number(ele))}
                callbackResetSelect={() => setUserMemberIds([])}
                keywordReset={isSearchDisplay}
              />
            ) : (
              <></>
            )
          }
          {
            callbackColorLabels ? (
              <div className="category">
                <div className="category_title">
                  カラーで絞り込み
                  {
                    colorLabels.length ? (
                      <div
                        className="select_reset"
                        role="none"
                        onClick={() => setColorLabels([])}
                      >
                        リセット
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
                <div className="chip_list">
                  {
                    COLOR_LIST.map((colorCategory) => (
                      <React.Fragment key={`color_category_${colorCategory.name}`}>
                        {
                          colorCategory.values.map((color) => (
                            <button
                              type="button"
                              className={`chip ${colorLabels.includes(color.label) ? "checked" : ""}`}
                              key={`color_${color.text}`}
                              onClick={() => clickColor(color.label)}
                            >
                              <div
                                style={{
                                  backgroundColor: color.color,
                                  width: "12px",
                                  height: "12px",
                                  borderRadius: "50%",
                                }}
                              />
                              {color.text}
                            </button>
                          ))
                        }
                      </React.Fragment>
                    ))
                  }
                </div>
              </div>
            ) : (
              <></>
            )
          }
        </div>
        <div className="button_area" style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "16px", gap: "40px" }}>
          <button
            type="button"
            className="button cancel"
            onClick={resetSearch}
          >
            絞り込み条件をリセット
          </button>
          <button
            type="button"
            className="button"
            onClick={submitSearch}
            disabled={!isCanSubmit}
          >
            絞り込む
          </button>
        </div>
      </div>
      <div className={`close_screen gray ${isSearchDisplay || 'none'}`} role="presentation" onClick={cancelSearch} />
    </>
  )
}

SearchModule.defaultProps = {
  callbackTeamIds: undefined,
  callbackMemberIds: undefined,
  callbackKeywords: undefined,
  callbackLabelMasterKeywords: undefined,
  callbackKey: undefined,
  callbackDirection: undefined,
  callbackDisabled: undefined,
  callbackPageOffset: undefined,
  callbackLabelGroupIds: undefined,
  callbackMasterRelationLabelGroupIds: undefined,
  callbackMasterRelationLabelGroupDisabled: undefined,
  callbackColorLabels: undefined,
  callbackCreatorMemberIds: undefined,
  callbackViewerMemberIds: undefined,
  callbackUserMemberIds: undefined,
  memberList: undefined,
  teamList: undefined,
  labelGroupList: undefined,
  masterRelationLabelGroupList: undefined,
  isClear: false,
}

export default SearchModule
